import { Box, Card, CardContent, FormControl, GridLegacy, InputLabel, MenuItem, Select, Switch, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { getPageTitle } from 'src/config/projectConfig';

/* ─── Toggle row ─── */
interface ToggleRowProps {
  label: string;
  defaultChecked?: boolean;
}

const ToggleRow = ({ label, defaultChecked = false }: ToggleRowProps) => {
  const [checked, setChecked] = useState(defaultChecked);
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
      <Typography variant="body2" fontSize={13} color={checked ? 'textPrimary' : 'textSecondary'}>
        {label}
      </Typography>
      <Switch
        size="small"
        checked={checked}
        onChange={() => setChecked((prev) => !prev)}
        color="primary"
      />
    </Box>
  );
};

/* ─── Toggle card with two-column layout ─── */
const ToggleCard = ({
  title,
  items,
}: {
  title: string;
  items: { id: string; label: string; defaultChecked?: boolean }[];
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <GridLegacy container spacing={0}>
        {items.map((item) => (
          <GridLegacy item xs={6} key={item.id}>
            <ToggleRow label={item.label} defaultChecked={item.defaultChecked} />
          </GridLegacy>
        ))}
      </GridLegacy>
    </CardContent>
  </Card>
);

const packageInfoItems = [
  { id: 'user-login-name', label: 'User login name', defaultChecked: false },
  { id: 'user-id', label: 'User ID', defaultChecked: true },
  { id: 'package', label: 'Package', defaultChecked: true },
  { id: 'quota', label: 'Quota', defaultChecked: false },
  { id: 'quota-bytes', label: 'Quota (bytes)', defaultChecked: false },
  { id: 'data-size', label: 'Data size', defaultChecked: false },
  { id: 'data-size-bytes', label: 'Data size (bytes)', defaultChecked: false },
  { id: 'retention', label: 'Retention', defaultChecked: false },
  { id: 'retention-bytes', label: 'Retention (bytes)', defaultChecked: false },
  { id: 'status', label: 'Status', defaultChecked: false },
  { id: 'version', label: 'Version (last run version)', defaultChecked: false },
  { id: 'last-job-ok', label: 'Last job OK', defaultChecked: false },
  { id: 'backup-server', label: 'Backup server', defaultChecked: true },
  { id: 'category', label: 'Category', defaultChecked: false },
];

const customerInfoItems = [
  { id: 'user-company', label: 'User company', defaultChecked: false },
  { id: 'telephone', label: 'Telephone', defaultChecked: false },
  { id: 'email', label: 'Email', defaultChecked: false },
  { id: 'create-date', label: 'Create date', defaultChecked: false },
  { id: 'contact-end-date', label: 'Contact end date', defaultChecked: false },
  { id: 'user-url', label: 'User URL', defaultChecked: false },
  { id: 'customer-last-job-ok', label: 'Last job OK', defaultChecked: false },
];

const backupSetItems = [
  { id: 'software-version', label: 'Software version', defaultChecked: false },
  { id: 'backup-set-id', label: 'Backup set ID', defaultChecked: false },
  { id: 'backup-complete-date', label: 'Backup complete date', defaultChecked: false },
  { id: 'os', label: 'OS', defaultChecked: false },
  { id: 'pc-server', label: 'PC/Server', defaultChecked: false },
  { id: 'retention-policy', label: 'Retention Policy', defaultChecked: false },
  { id: 'backup-set-status', label: 'Status', defaultChecked: false },
  { id: 'backup-set-last-job-ok', label: 'Last job OK', defaultChecked: false },
];

const recentActivity = [
  { id: 'la1', text: 'New case assigned to agent Anya Forger', date: '15 Mar 2026 - 14:30' },
  { id: 'la2', text: "Case status updated to 'In Progress' by agent Loid Forger", date: '14 Mar 2026 - 09:15' },
  { id: 'la3', text: 'New comment added to case by client Damian Desmond', date: '13 Mar 2026 - 16:45' },
  { id: 'la4', text: "Case reassigned to team 'Alpha' by manager Sylvia Sherwood", date: '13 Mar 2026 - 10:00' },
  { id: 'la5', text: 'New case created by client Yor Briar', date: '12 Mar 2026 - 18:20' },
  { id: 'la6', text: 'New case assigned to agent Anya Forger', date: '15 Mar 2026 - 14:30' },
  { id: 'la7', text: "Case status updated to 'In Progress' by agent Loid Forger", date: '14 Mar 2026 - 09:15' },
  { id: 'la8', text: 'New comment added to case by client Damian Desmond', date: '13 Mar 2026 - 16:45' },
];

const Lists = () => (
  <>
    <Helmet>
      <title>{getPageTitle('Lists')}</title>
    </Helmet>

    <Box p={3}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Lists
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom mb={3}>
        Manage data to be included in the list.
      </Typography>

      <GridLegacy container spacing={3}>
        <GridLegacy item xs={12} md={3}>
          <ToggleCard title="Package Information" items={packageInfoItems} />
        </GridLegacy>
        <GridLegacy item xs={12} md={3}>
          <ToggleCard title="Customer Information" items={customerInfoItems} />
        </GridLegacy>
        <GridLegacy item xs={12} md={3}>
          <ToggleCard title="Backup Set Details" items={backupSetItems} />
        </GridLegacy>
        <GridLegacy item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                List Settings
              </Typography>
              <GridLegacy container spacing={0}>
                <GridLegacy item xs={6}>
                  <ToggleRow label="Column headers" defaultChecked />
                </GridLegacy>
                <GridLegacy item xs={6}>
                  <ToggleRow label="Colors" defaultChecked />
                </GridLegacy>
              </GridLegacy>

              <Box mt={2}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: 13 }}>Sort on</InputLabel>
                  <Select defaultValue="Company" label="Sort on" sx={{ fontSize: 13 }}>
                    <MenuItem value="Company" sx={{ fontSize: 13 }}>Company</MenuItem>
                    <MenuItem value="Name" sx={{ fontSize: 13 }}>Name</MenuItem>
                    <MenuItem value="Date" sx={{ fontSize: 13 }}>Date</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box mt={2}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: 13 }}>Order</InputLabel>
                  <Select defaultValue="Ascending" label="Order" sx={{ fontSize: 13 }}>
                    <MenuItem value="Ascending" sx={{ fontSize: 13 }}>Ascending</MenuItem>
                    <MenuItem value="Descending" sx={{ fontSize: 13 }}>Descending</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </GridLegacy>
      </GridLegacy>

      {/* Recent Activity */}
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box px={2.5} pt={2} pb={1}>
            <Typography variant="h6" fontWeight={600}>
              Recent Activity
            </Typography>
          </Box>
          {recentActivity.map((item) => (
            <Box
              key={item.id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={2.5}
              py={1.25}
              borderBottom="1px solid #EDF2F7"
            >
              <Typography variant="body2" fontSize={13} color="textPrimary">
                {item.text}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5} whiteSpace="nowrap" ml={2}>
                <Typography variant="caption" fontSize={12} sx={{ color: '#38B2AC' }}>
                  {item.date}
                </Typography>
                <PersonIcon sx={{ fontSize: 14, color: '#A0AEC0' }} />
                <Typography variant="caption" fontSize={12} color="textSecondary">
                  System
                </Typography>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  </>
);

export default Lists;
